/*!
 * TinyPoker MTT 數據統計
 * 顯示 MTT 比賽的真人 / EcoBot 玩家數與報名發數
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Table,
  Badge,
  Alert,
  Input,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import axios from "axios";
import { COLORS } from "constant/adminTheme";
import TinyLoading, { TinyBusy, TinyLoadingOverlay } from "components/tiny/TinyLoading";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import TinyPageHeader from "components/tiny/TinyPageHeader";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const API_BASE = "/api/tiny";

const numberWithCommas = (num) => {
  if (num === null || num === undefined) return "0";
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const nDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const { accentGradient: ACCENT_GRADIENT } = COLORS;

const inputStyle = {
  backgroundColor: "#27293d",
  color: "#fff",
  border: "1px solid #2b3553",
  // ⚠️ 這一列的輸入框、開關 chip 與查詢鈕高度要一致，否則底線對不齊
  height: 38,
  borderRadius: 8,
};

const TREND_METRICS = [
  { key: "real_player_count", label: "真人玩家數", color: "#1f8ef1" },
  { key: "real_entry_count", label: "真人報名發數", color: "#1d8cf8" },
  { key: "ecobot_player_count", label: "EcoBot 玩家數", color: "#00d6b4" },
  { key: "ecobot_entry_count", label: "EcoBot 報名發數", color: "#f5a623" },
  { key: "ecobot_prize", label: "EcoBot 獲獎金額", color: "#ff6b6b" },
  { key: "entries", label: "總報名數", color: "#ba54f5" },
  { key: "gtd_prize", label: "GTD", color: "#e14eca" },
  { key: "payout_prize", label: "獎池", color: "#1d8cf8" },
  { key: "overlay", label: "Overlay", color: "#fd5d93" },
];

function TinyMttStats() {
  const [mode, setMode] = useState("range");
  const [startDate, setStartDate] = useState(nDaysAgo(7));
  const [endDate, setEndDate] = useState(todayStr());
  const [startHour, setStartHour] = useState("20:00");
  const [endHour, setEndHour] = useState("23:00");
  const [overlayOnly, setOverlayOnly] = useState(false);
  const [freerollOnly, setFreerollOnly] = useState(false);
  const [titleSearch, setTitleSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadingText, setLoadingText] = useState("載入 MTT 數據");
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState("time.start");
  const [sortOrder, setSortOrder] = useState(-1);

  const [trendModal, setTrendModal] = useState(false);
  const [trendTitle, setTrendTitle] = useState("");
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(null);
  const [trendMetric, setTrendMetric] = useState("real_player_count");

  const didMount = useRef(false);

  const openTrendModal = async (title) => {
    setTrendTitle(title);
    setTrendModal(true);
    setTrendLoading(true);
    setTrendError(null);
    setTrendData(null);
    try {
      const res = await axios.get(`${API_BASE}/mtt-stats/trend`, {
        params: { title },
      });
      if (res.data.code === 1) {
        setTrendData(res.data.data);
      } else {
        setTrendError(res.data.msg || "查詢失敗");
      }
    } catch (e) {
      setTrendError(e.response?.data?.msg || e.message || "請求失敗");
    } finally {
      setTrendLoading(false);
    }
  };

  const closeTrendModal = () => {
    setTrendModal(false);
    setTrendTitle("");
    setTrendData(null);
    setTrendError(null);
  };

  const getTrendChartData = () => {
    if (!trendData?.data_points?.length) return null;
    const metric = TREND_METRICS.find((m) => m.key === trendMetric) || TREND_METRICS[0];
    const labels = trendData.data_points.map((p) => {
      const d = p.date || "";
      return d.length >= 16 ? d.slice(5, 16) : d;
    });
    return {
      labels,
      datasets: [
        {
          label: metric.label,
          data: trendData.data_points.map((p) => p[metric.key] ?? 0),
          borderColor: metric.color,
          backgroundColor: metric.color + "33",
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 7,
          pointBackgroundColor: metric.color,
          borderWidth: 2,
        },
      ],
    };
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: { color: "#fff", font: { size: 12 }, usePointStyle: true },
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.85)",
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        callbacks: {
          title: (items) => {
            if (!items.length || !trendData?.data_points) return "";
            return trendData.data_points[items[0].dataIndex]?.date || "";
          },
          label: (ctx) => {
            let label = ctx.dataset.label || "";
            if (label) label += ": ";
            label += numberWithCommas(ctx.parsed.y);
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: "#778899", font: { size: 11 }, maxRotation: 45 },
        grid: { color: "rgba(255,255,255,0.06)" },
      },
      y: {
        ticks: {
          color: "#778899",
          font: { size: 11 },
          callback: (v) => {
            if (v >= 1000000) return (v / 1000000).toFixed(1) + "M";
            if (v >= 1000) return (v / 1000).toFixed(0) + "K";
            return v;
          },
        },
        grid: { color: "rgba(255,255,255,0.06)" },
        beginAtZero: true,
      },
    },
  };

  const fetchData = useCallback(
    async (p, ps, overrideMode, sf, so) => {
      const m = overrideMode || mode;
      const field = sf || sortField;
      const order = so !== undefined ? so : sortOrder;
      setLoading(true);
      setLoadingText("載入 MTT 數據");
      setError(null);
      try {
        const params = {
          mode: m, page: p, page_size: ps,
          sort_field: field, sort_order: order,
        };
        if (m === "daily") {
          params.start_hour = startHour;
          params.end_hour = endHour;
        } else {
          params.start_date = startDate;
          params.end_date = endDate;
        }
        if (overlayOnly) params.overlay_only = "1";
        if (freerollOnly) params.freeroll_only = "1";
        if (titleSearch.trim()) params.title_search = titleSearch.trim();
        const res = await axios.get(`${API_BASE}/mtt-stats/tournaments`, { params });
        if (res.data.code === 1) {
          setData(res.data.data);
        } else {
          setError(res.data.msg || "查詢失敗");
        }
      } catch (e) {
        setError(e.response?.data?.msg || e.message || "請求失敗");
      } finally {
        setLoading(false);
      }
    },
    [mode, startDate, endDate, startHour, endHour, overlayOnly, freerollOnly, titleSearch, sortField, sortOrder]
  );

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      fetchData(1, pageSize);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModeSwitch = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    setPage(1);
    setData(null);
    setTimeout(() => {
      fetchData(1, pageSize, newMode);
    }, 0);
  };

  const handleSearch = () => {
    setPage(1);
    fetchData(1, pageSize);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage, pageSize);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
    fetchData(1, newSize);
  };

  const handleSort = (field) => {
    const newOrder = sortField === field ? -sortOrder : -1;
    setSortField(field);
    setSortOrder(newOrder);
    setPage(1);
    fetchData(1, pageSize, undefined, field, newOrder);
  };

  const pagination = data?.pagination;
  const tournaments = data?.tournaments || [];
  const summary = data?.summary;

  return (
    <div className="content">
      <TinyLoadingOverlay fixed active={loading} text={loadingText}>

      {/* ⚠️「日期範圍 / 每日時段」是**子視圖切換**（同一批資料的兩種看法），
          所以它是 tab 不是 filter，改用公版。
          底下 CardBody 的日期輸入是**篩選表單**，留在原地那張卡。 */}
      <TinyPageHeader
        title="MTT 數據統計"
        description="錦標賽的報名、獎金與服務費表現，可看日期區間或每日時段分布。"
        tabs={{
          items: [
            { key: "range", label: "日期範圍", icon: "fa fa-calendar" },
            { key: "daily", label: "每日時段", icon: "fa fa-clock" },
          ],
          active: mode,
          onChange: handleModeSwitch,
        }}
      />

      {/* ========== 篩選區 ========== */}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <Row className="align-items-end">
                {mode === "daily" ? (
                  <>
                    <Col md="3">
                      <FormGroup style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "12px", color: "#9A9A9A" }}>開始時間</label>
                        <Input type="time" value={startHour} onChange={(e) => setStartHour(e.target.value)} style={inputStyle} />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "12px", color: "#9A9A9A" }}>結束時間</label>
                        <Input type="time" value={endHour} onChange={(e) => setEndHour(e.target.value)} style={inputStyle} />
                      </FormGroup>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col md="3">
                      <FormGroup style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "12px", color: "#9A9A9A" }}>開始日期</label>
                        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "12px", color: "#9A9A9A" }}>結束日期</label>
                        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
                      </FormGroup>
                    </Col>
                  </>
                )}
                <Col md="2">
                  <FormGroup style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: "12px", color: "#9A9A9A" }}>比賽名稱搜尋</label>
                    <Input
                      type="text"
                      placeholder="輸入關鍵字..."
                      value={titleSearch}
                      onChange={(e) => setTitleSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      style={inputStyle}
                    />
                  </FormGroup>
                </Col>
                {/* ⚠️ 這兩個布林原本是原生 `<input type="checkbox">` 塞在 md=1 裡：
                       瀏覽器預設的方框跟整站的 design token 完全不同調，
                       欄寬又太窄，「僅 Overlay」被擠成兩行（Brad 2026-09-10 截圖）。
                    → 改成**開關 chip**（同 B端數據分析的真人／ECOBOT／自營BOT），
                       關閉是描邊、開啟是該項目本來的語意色淡染 + 實色邊。
                    🔑 這一欄外面包一層隱形 `<label>` 佔位 —— 旁邊三欄都有標題列，
                       沒有的話這一欄會整個往上跑、跟輸入框對不齊。 */}
                <Col md="3">
                  <FormGroup style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: "12px", color: "#9A9A9A" }}>篩選條件</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", height: 38 }}>
                      {[
                        { on: overlayOnly, set: setOverlayOnly, label: "僅 Overlay", color: "#fd5d93", icon: "fa-arrow-trend-down" },
                        { on: freerollOnly, set: setFreerollOnly, label: "僅免費賽", color: "#f5a623", icon: "fa-gift" },
                      ].map((c) => (
                        <button
                          key={c.label}
                          type="button"
                          onClick={() => c.set(!c.on)}
                          aria-pressed={c.on}
                          style={{
                            height: 32,
                            boxSizing: "border-box",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "0 12px",
                            margin: 0,
                            borderRadius: 8,
                            // 邊框永遠 1px、只換顏色 → 開關切換時不會有尺寸跳動
                            border: `1px solid ${c.on ? c.color : "rgba(255,255,255,0.18)"}`,
                            background: c.on ? `${c.color}26` : "transparent",
                            color: c.on ? c.color : "#9A9A9A",
                            fontSize: 12.5,
                            fontWeight: c.on ? 600 : 400,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                            transition: "background 0.16s ease, border-color 0.16s ease, color 0.16s ease",
                          }}
                        >
                          <i className={`fa ${c.on ? "fa-check" : c.icon}`} style={{ fontSize: 11 }} />
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </FormGroup>
                </Col>
                <Col md="1">
                  <FormGroup style={{ marginBottom: 0 }}>
                    {/* 同上：隱形標題列讓按鈕跟輸入框底部對齊 */}
                    <label style={{ fontSize: "12px", color: "transparent", userSelect: "none" }}>查詢</label>
                    <button
                      type="button"
                      onClick={handleSearch}
                      disabled={loading}
                      style={{
                        width: "100%",
                        height: 38,
                        boxSizing: "border-box",
                        margin: 0,
                        border: "none",
                        borderRadius: 8,
                        background: ACCENT_GRADIENT,
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 600,
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                        opacity: loading ? 0.55 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                    >
                      {loading ? <TinyBusy light style={{ marginRight: 4 }} /> : <i className="fa fa-search mr-1" />}查詢
                    </button>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {error && (
        <Alert color="danger">
          <i className="fa fa-exclamation-triangle mr-2"></i>
          {error}
        </Alert>
      )}

      {/* ========== Summary Cards ========== */}
      {summary && (
        <Row>
          {[
            { label: "真人報名發數", value: summary.real_entries, color: "#1d8cf8", icon: "fa-user" },
            { label: "EcoBot 報名發數", value: summary.ecobot_entries, color: "#f5a623", icon: "fa-robot" },
            { label: "EcoBot 獲獎總額", value: summary.ecobot_prize, color: "#ff6b6b", icon: "fa-robot" },
            { label: "Overlay 總和", value: summary.total_overlay, color: "#fd5d93", icon: "fa-exclamation-circle" },
          ].map((item, idx) => (
            <Col key={idx} style={{ flex: "1", minWidth: "160px" }}>
              <Card>
                <CardBody>
                  <Row>
                    <Col>
                      <p className="card-category" style={{ fontSize: "12px", color: "#9A9A9A" }}>
                        {item.label}
                        {summary.total_tournaments > 0 && (
                          <span style={{ marginLeft: "6px", fontSize: "11px", color: "#555" }}>
                            ({summary.total_tournaments} 場)
                          </span>
                        )}
                      </p>
                      <CardTitle tag="h3" style={{ color: item.color, marginBottom: 0 }}>
                        {numberWithCommas(item.value)}
                      </CardTitle>
                    </Col>
                    <Col className="text-right" style={{ maxWidth: "60px" }}>
                      <div
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          background: `${item.color}22`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i className={`fa ${item.icon}`} style={{ color: item.color, fontSize: "18px" }}></i>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* ========== 資料表格 ========== */}
      {data && (
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="6">
                    <CardTitle tag="h4" style={{ marginBottom: 0 }}>
                      比賽列表
                      {pagination && (
                        <Badge color="info" style={{ marginLeft: "10px", fontSize: "0.75em", verticalAlign: "middle" }}>
                          共 {pagination.total} 場
                        </Badge>
                      )}
                    </CardTitle>
                  </Col>
                  <Col md="6" className="text-right">
                    <span style={{ color: "#9A9A9A", fontSize: "13px", marginRight: "8px" }}>每頁</span>
                    <Input
                      type="select"
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      style={{ ...inputStyle, width: "80px", display: "inline-block", height: "32px", padding: "2px 8px", fontSize: "13px" }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Input>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody style={{ padding: 0, overflowX: "auto" }}>
                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>
                        比賽名稱
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "10px",
                            color: "#1f8ef1",
                            fontWeight: 400,
                            opacity: 0.7,
                            letterSpacing: "0.3px",
                          }}
                        >
                          <i className="fa fa-line-chart" style={{ marginRight: "3px", fontSize: "9px" }}></i>
                          點擊可查看趨勢
                        </span>
                      </th>
                      <th
                        onClick={() => handleSort("time.start")}
                        style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                      >
                        開始時間{" "}
                        {sortField === "time.start" ? (
                          <i className={`fa fa-sort-${sortOrder === 1 ? "up" : "down"}`} style={{ opacity: 1 }}></i>
                        ) : (
                          <i className="fa fa-sort" style={{ opacity: 0.3 }}></i>
                        )}
                      </th>
                      <th>時長</th>
                      <th>買入</th>
                      <th>盲注結構</th>
                      {[
                        { label: "真人玩家", field: "real_player_count", color: "#1f8ef1" },
                        { label: "真人報名發數", field: "real_entry_count", color: "#1d8cf8" },
                        { label: "EcoBot 玩家", field: "ecobot_player_count", color: "#00d6b4" },
                        { label: "EcoBot 報名發數", field: "ecobot_entry_count", color: "#f5a623" },
                        { label: "EcoBot 獲獎", field: "ecobot_prize", color: "#ff6b6b" },
                        { label: "總報名數", field: "entries" },
                        { label: "GTD", field: "gtd_prize" },
                        { label: "獎池", field: "payout_prize" },
                        { label: "Overlay", field: "overlay" },
                      ].map((col) => (
                        <th
                          key={col.field}
                          onClick={() => handleSort(col.field)}
                          style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", color: col.color }}
                        >
                          {col.label}{" "}
                          {sortField === col.field ? (
                            <i className={`fa fa-sort-${sortOrder === 1 ? "up" : "down"}`} style={{ opacity: 1 }}></i>
                          ) : (
                            <i className="fa fa-sort" style={{ opacity: 0.3 }}></i>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tournaments.length === 0 ? (
                      <tr>
                        <td colSpan={14} className="text-center" style={{ padding: "40px", color: "#9A9A9A" }}>
                          <i className="fa fa-inbox" style={{ fontSize: "28px", display: "block", marginBottom: "8px" }}></i>
                          無資料，請調整篩選條件後查詢
                        </td>
                      </tr>
                    ) : (
                      tournaments.map((t, idx) => (
                        <tr key={idx}>
                          <td style={{ maxWidth: "220px", padding: "8px 12px" }}>
                            <span
                              title="點擊查看歷史趨勢"
                              onClick={() => openTrendModal(t.tournament_title)}
                              style={{
                                fontWeight: 500,
                                cursor: "pointer",
                                color: "#e3e3e3",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "3px 8px",
                                borderRadius: "4px",
                                transition: "all 0.15s ease",
                                maxWidth: "100%",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "rgba(31,142,241,0.12)";
                                e.currentTarget.style.color = "#1f8ef1";
                                e.currentTarget.querySelector(".trend-icon").style.opacity = "1";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = "#e3e3e3";
                                e.currentTarget.querySelector(".trend-icon").style.opacity = "0";
                              }}
                            >
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {t.tournament_title}
                              </span>
                              <i
                                className="fa fa-line-chart trend-icon"
                                style={{ fontSize: "10px", opacity: 0, color: "#1f8ef1", transition: "opacity 0.15s ease", flexShrink: 0 }}
                              ></i>
                            </span>
                          </td>
                          <td style={{ whiteSpace: "nowrap", fontSize: "0.9em" }}>{t.time?.start || "-"}</td>
                          <td style={{ fontSize: "0.9em", color: "#9A9A9A" }}>{t.time?.duration || "-"}</td>
                          <td>
                            <span style={{ color: "#f5a623" }}>{numberWithCommas(t.buy_in?.buy_in || 0)}</span>
                            {t.buy_in?.fee > 0 && <span style={{ color: "#9A9A9A", fontSize: "0.85em" }}>+{t.buy_in.fee}</span>}
                          </td>
                          <td>
                            <Badge color="info" style={{ fontSize: "0.85em" }}>{t.structure?.blinds || "-"}</Badge>
                          </td>
                          <td style={{ fontWeight: 600, color: "#1f8ef1" }}>{numberWithCommas(t.real_player_count)}</td>
                          <td style={{ fontWeight: 600, color: "#1d8cf8" }}>{numberWithCommas(t.real_entry_count)}</td>
                          <td style={{ fontWeight: 600, color: "#00d6b4" }}>{numberWithCommas(t.ecobot_player_count)}</td>
                          <td style={{ fontWeight: 600, color: "#f5a623" }}>{numberWithCommas(t.ecobot_entry_count)}</td>
                          <td style={{ fontWeight: 600, color: "#ff6b6b" }}>{numberWithCommas(t.ecobot_prize)}</td>
                          <td style={{ fontWeight: 500 }}>
                            {numberWithCommas(t.entries)}
                            {t.ticket_entries > 0 && (
                              <div style={{ fontSize: "0.85em", color: "#e0a040", marginTop: "2px" }}>
                                🎫 {t.ticket_entries}
                              </div>
                            )}
                          </td>
                          <td>{numberWithCommas(t.gtd_prize)}</td>
                          <td>{numberWithCommas(t.payout_prize)}</td>
                          <td>
                            <span style={{ color: t.overlay > 0 ? "#fd5d93" : "#9A9A9A", fontWeight: t.overlay > 0 ? 600 : 400 }}>
                              {numberWithCommas(t.overlay)}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>

                {/* ========== 分頁 ========== */}
                {pagination && pagination.total > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: "1px solid #2b3553" }}>
                    <span style={{ color: "#9A9A9A", fontSize: "13px" }}>
                      第 {pagination.page} / {pagination.total_pages} 頁，共 {pagination.total} 場
                    </span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      {[
                        { icon: "fa-angle-double-left", p: 1, disabled: pagination.page <= 1 },
                        { icon: "fa-angle-left", p: pagination.page - 1, disabled: pagination.page <= 1 },
                        { icon: "fa-angle-right", p: pagination.page + 1, disabled: pagination.page >= pagination.total_pages },
                        { icon: "fa-angle-double-right", p: pagination.total_pages, disabled: pagination.page >= pagination.total_pages },
                      ].map((btn, i) => (
                        <button
                          key={i}
                          className={`btn btn-sm ${btn.disabled ? "btn-secondary" : "btn-info"}`}
                          disabled={btn.disabled}
                          onClick={() => handlePageChange(btn.p)}
                          style={{ padding: "4px 10px" }}
                        >
                          <i className={`fa ${btn.icon}`}></i>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* ========== 趨勢曲線圖 Modal ========== */}
      {trendModal && (
        <style>{`
          .trend-modal-wrapper .modal-content {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
          .trend-modal-wrapper + .modal-backdrop,
          .trend-modal-wrapper ~ .modal-backdrop {
            background: rgba(0,0,0,0.7) !important;
            opacity: 1 !important;
          }
        `}</style>
      )}
      <Modal
        isOpen={trendModal}
        toggle={closeTrendModal}
        size="xl"
        style={{ maxWidth: "920px" }}
        modalClassName="trend-modal-wrapper"
      >
        <div
          style={{
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
            borderRadius: "12px",
            border: "1px solid rgba(31,142,241,0.25)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(31,142,241,0.08)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "18px 24px",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "rgba(0,0,0,0.15)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #1f8ef1 0%, #5e72e4 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className="fa fa-line-chart" style={{ color: "#fff", fontSize: "14px" }}></i>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "10px", color: "#8899aa", textTransform: "uppercase", letterSpacing: "1px", lineHeight: 1 }}>
                  歷史趨勢
                </div>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#fff",
                    marginTop: "2px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {trendTitle}
                </div>
              </div>
            </div>
            <button
              onClick={closeTrendModal}
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "8px",
                color: "#8899aa",
                cursor: "pointer",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px",
                transition: "all 0.15s ease",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.12)";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                e.currentTarget.style.color = "#8899aa";
              }}
            >
              <i className="fa fa-times"></i>
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: "20px 24px", minHeight: "420px" }}>
            {trendLoading && (
              <TinyLoading variant="block" text="載入趨勢資料" style={{ padding: "100px 0" }} />
            )}
            {trendError && (
              <Alert color="danger" style={{ borderRadius: "8px" }}>
                <i className="fa fa-exclamation-triangle mr-2"></i>
                {trendError}
              </Alert>
            )}
            {!trendLoading && !trendError && trendData && (
              <>
                {/* Metric selector */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <label style={{ fontSize: "13px", color: "#8899aa", marginBottom: 0, whiteSpace: "nowrap" }}>選擇指標</label>
                    <Input
                      type="select"
                      value={trendMetric}
                      onChange={(e) => setTrendMetric(e.target.value)}
                      style={{
                        backgroundColor: "rgba(255,255,255,0.05)",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: "6px",
                        width: "200px",
                        height: "34px",
                        fontSize: "13px",
                      }}
                    >
                      {TREND_METRICS.map((m) => (
                        <option key={m.key} value={m.key}>{m.label}</option>
                      ))}
                    </Input>
                  </div>
                  {trendData.data_points?.length > 0 && (
                    <span style={{ fontSize: "12px", color: "#556677" }}>
                      共 {trendData.data_points.length} 場歷史記錄
                    </span>
                  )}
                </div>

                {/* Chart area */}
                {trendData.data_points?.length > 0 ? (
                  <div
                    style={{
                      background: "rgba(0,0,0,0.2)",
                      borderRadius: "10px",
                      border: "1px solid rgba(255,255,255,0.05)",
                      padding: "20px 16px 12px 8px",
                      height: "360px",
                    }}
                  >
                    <Line data={getTrendChartData()} options={trendChartOptions} />
                  </div>
                ) : (
                  <div
                    className="text-center"
                    style={{
                      padding: "80px 0",
                      color: "#556677",
                      background: "rgba(0,0,0,0.15)",
                      borderRadius: "10px",
                      border: "1px solid rgba(255,255,255,0.04)",
                    }}
                  >
                    <i className="fa fa-inbox" style={{ fontSize: "36px", display: "block", marginBottom: "12px" }}></i>
                    此賽事無歷史資料
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "12px 24px",
              borderTop: "1px solid rgba(255,255,255,0.06)",
              display: "flex",
              justifyContent: "flex-end",
              background: "rgba(0,0,0,0.1)",
            }}
          >
            <button
              className="btn btn-sm"
              onClick={closeTrendModal}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "#ccc",
                borderRadius: "6px",
                padding: "5px 18px",
                fontSize: "13px",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.14)";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                e.currentTarget.style.color = "#ccc";
              }}
            >
              關閉
            </button>
          </div>
        </div>
      </Modal>
      </TinyLoadingOverlay>
    </div>
  );
}

export default TinyMttStats;
